import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Parser from "html-react-parser";
import { Toast } from "../../Components";
import { motion } from "framer-motion";
import { accountService } from "../../Services";
import ReCAPTCHA from "react-google-recaptcha";

const PASSWORD_REGEX = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

export default function CreateAccount({ setModal, setRoleId, setReferId }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const roleType = searchParams.get("type");
  const initialReferId =
    searchParams.get("refId") || sessionStorage.getItem("referId");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // eslint-disable-next-line 
  const [referId, setLocalReferId] = useState(initialReferId);
  const [error, setError] = useState({});
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isBrandSelected, setIsBrandSelected] = useState(
    roleType !== "influencer" && roleType !== "1"
  );
  const [toastList, setToastList] = useState([]);
  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignupWithEmail, setIsSignupWithEmail] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
    link.crossOrigin = "anonymous";
    link.referrerPolicy = "no-referrer";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    if (referId) {
      sessionStorage.setItem("referId", referId);
    }

    const errorCode = searchParams.get("error");
    let errorMessage = "";
    if (errorCode === "1") {
      errorMessage = Parser(
        "Account already exists <a className='paidLink' href='/#/account/login'> click here </a> to login"
      );
    } else if (errorCode === "2") {
      errorMessage = Parser("Not have enough permission to signup");
    } else if (errorCode === "3") {
      errorMessage = Parser(
        "We encountered an error processing your request. Please try again later."
      );
    }

    if (errorMessage) {
      addToast({ error: true, msg: errorMessage });
    }
    // eslint-disable-next-line 
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && isSignupWithEmail) createValidation();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line 
  }, [form, isSignupWithEmail]);

  const updateForm = (data) => setForm((prev) => ({ ...prev, ...data }));
  const setInput = (key) => (event) => {
    updateForm({ [key]: event.target.value });
    if (error[key]) {
      setError((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const addToast = (data) => {
    setToastList((prev) => [
      ...prev,
      { ...data, id: `${Date.now()}-${prev.length}` },
    ]);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Invalid email format";
    if (!form.password) newErrors.password = "Password is required";
    else if (!PASSWORD_REGEX.test(form.password)) {
      newErrors.password =
        "Min 8 chars with uppercase, lowercase, number & special char";
    }
    if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Passwords don't match";
    if (!captcha) newErrors.captcha = "Please complete the captcha";
    if (!isTermsChecked) newErrors.terms = "You must accept the terms";

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createValidation = () => {
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      title: "",
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      confirmPassword: form.confirmPassword,
      refId: referId,
      role: isBrandSelected ? "advertiser" : "influencer",
      acceptTerms: isTermsChecked,
      captcha,
    };

    accountService
      .register(payload)
      .then((ressignup) => {
        if (ressignup.data.paypalurl && ressignup.data.paypalurl !== "") {
          accountService
            .login(form.email, form.password)
            .then((res) => {
              window.location.replace(ressignup.data.paypalurl);
            })
            .catch((loginErr) => {
              let errorMsg = String(loginErr).replace(/<\/?[^>]+(>|$)/gi, "");
              try {
                const parsed = JSON.parse(errorMsg);
                errorMsg = parsed.message || errorMsg;
              } catch (e) {}
              setToastList([
                ...toastList,
                { error: true, msg: errorMsg, id: Date.now() },
              ]);
            });
        } else {
          navigate("/account/email-verification", {
            state: { id: ressignup.data.id, showSuccessToast: true },
          });
        }
      })
      .catch((err) => {
        let errorMsg = String(err).replace(/<\/?[^>]+(>|$)/gi, "");
        try {
          const parsed = JSON.parse(errorMsg);
          errorMsg = parsed.message || errorMsg;
        } catch (e) {}

        // Check if error is about email already exists
        if (
          errorMsg.toLowerCase().includes("email") ||
          errorMsg.toLowerCase().includes("already")
        ) {
          setError({ ...error, email: errorMsg });
        } else {
          setToastList([
            ...toastList,
            { error: true, msg: errorMsg, id: Date.now() },
          ]);
        }
      })
      .finally(() => setLoading(false));

    // accountService
    //   .register(payload)
    //   .then((res) => {
    //     navigate("/account/validateEmail", {
    //       state: { email: res.data.email, userId: res.data.id },
    //     });
    //   })
    //   .catch((err) => {
    //     addToast({ error: true, msg: err.replace(/<\/?[^>]+(>|$)/gi, "") });
    //   })
    //   .finally(() => setLoading(false));
  };

  const handleSocialLogin = (platform) => {
    if (!isTermsChecked) {
      setError({ terms: "You must accept the terms" });
      return;
    }
    const roleID = isBrandSelected ? 3 : 1;
    setRoleId(isBrandSelected ? "I'm a brand" : "I'm a influencer");
    setReferId(referId);
    window.location.href = `${process.env.REACT_APP_API_URL}/login/${platform}/action/signup/role/${roleID}/${referId}/referalPath`;
  };

  // Social icons
  const FacebookIcon = () => (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <path
        fill="#1877F2"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );

  const InstagramIcon = () => (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <defs>
        <linearGradient
          id="instagram-gradient-create"
          x1="0%"
          y1="100%"
          x2="100%"
          y2="0%"
        >
          <stop offset="0%" stopColor="#FFDC80" />
          <stop offset="25%" stopColor="#F77737" />
          <stop offset="50%" stopColor="#E1306C" />
          <stop offset="75%" stopColor="#C13584" />
          <stop offset="100%" stopColor="#833AB4" />
        </linearGradient>
      </defs>
      <path
        fill="url(#instagram-gradient-create)"
        d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
      />
    </svg>
  );

  const TikTokIcon = () => (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <path
        fill="#000000"
        d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"
      />
    </svg>
  );

  const GoogleIcon = () => (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  const EmailIcon = () => (
    <svg
      className="w-5 h-5 sm:w-6 sm:h-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect
        x="2"
        y="4"
        width="20"
        height="16"
        rx="2"
        className="stroke-gray-600"
      />
      <path d="M22 6L12 13L2 6" className="stroke-gray-600" />
    </svg>
  );

  // Logo component
  const Logo = () => (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-lg"
        style={{
          background:
            "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
          boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
        }}
      >
        <img
          src={process.env.PUBLIC_URL + "/favicon/favicon.ico"}
          className="h-5 w-5 sm:h-7 sm:w-7 drop-shadow-md"
          alt="Logo"
        />
      </div>
      <span
        className="font-bold text-xl sm:text-2xl tracking-wide"
        style={{
          background: "linear-gradient(90deg, #059669, #10b981, #34d399)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        TAMA
      </span>
    </div>
  );

  // Mockup data
  const mockupUsers = [
    {
      name: "Maya Philips",
      followers: "4.5M users",
      color: "bg-pink-500",
      initial: "M",
    },
    {
      name: "Claire Writers",
      followers: "4M users",
      color: "bg-purple-500",
      initial: "C",
    },
    {
      name: "Austin Karter",
      followers: "3M users",
      color: "bg-blue-500",
      initial: "A",
    },
    {
      name: "Abram Daloda",
      followers: "2.5M users",
      color: "bg-emerald-500",
      initial: "A",
    },
  ];

  // Social signup buttons
  const socialButtons = [
    { provider: "facebook", label: "Facebook", Icon: FacebookIcon },
    { provider: "instagram", label: "Instagram", Icon: InstagramIcon },
    { provider: "tiktok", label: "TikTok", Icon: TikTokIcon },
    { provider: "google", label: "Google", Icon: GoogleIcon },
  ];

  const typeInfo = isBrandSelected
    ? {
        title: "Brand",
        icon: "fa-building",
        color: "from-emerald-500  to-green-600",
        bgColor: "from-emerald-50 to-green-50",
      }
    : {
        title: "Influencer",
        icon: "fa-star",
        color: "from-pink-500 to-rose-600",
        bgColor: "from-pink-50 to-rose-50",
      };

  return (
    <div className="flex min-h-screen font-sans">
      {/* Left Panel - Desktop only */}
      <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-8 bg-gradient-to-br from-blue-50 via-green-50 to-pink-50 lg:min-w-[50%] relative overflow-hidden">
        <div className="text-center max-w-[500px] z-10">
          <div className="flex items-start justify-center gap-3 mb-8">
            <span
              className="text-4xl font-extrabold"
              style={{
                background:
                  "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              #1
            </span>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight text-left">
              End-to-End Influencer
              <br />
              Marketing Platform
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 mb-8 w-full max-w-[450px]">
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4">
              <span className="text-sm font-semibold text-gray-700">
                Influencer Revenue Performance
              </span>
              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  Reach
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                  Engagement
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {mockupUsers.map((user, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-400 mr-2">
                      {index + 1}
                    </span>
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${user.color}`}
                    >
                      {user.initial}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {user.name}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500">
                    {user.followers}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Award-Winning Technology Section */}
          <div className="mt-10 w-full max-w-[650px]">
            <h3 className="text-lg font-semibold text-gray-800 mb-6 text-center">
              Award-Winning Technology
            </h3>
            <div className="flex items-center justify-between w-full">
              <img
                src={process.env.PUBLIC_URL + "/images/trusted.png"}
                alt="Trusted"
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img
                src={process.env.PUBLIC_URL + "/images/high_performance.png"}
                alt="High Performance"
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img
                src={process.env.PUBLIC_URL + "/images/powerd_by_data.png"}
                alt="Powered by Data"
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img
                src={process.env.PUBLIC_URL + "/images/recommended.png"}
                alt="Recommended"
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img
                src={process.env.PUBLIC_URL + "/images/responsible_ai.png"}
                alt="Responsible AI"
                className="h-20 object-contain hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div
        className="w-full lg:flex-1 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-8 bg-white lg:min-w-[50%] min-h-screen lg:min-h-0 overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#e5e7eb transparent",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-[400px] px-2 sm:px-0"
        >
          {/* Logo */}
          <div className="flex items-center justify-center mb-4 sm:mb-6">
            <Logo />
          </div>

          {/* Account Type Badge */}
          {/* <div
            className={`flex items-center justify-center gap-2 mb-4 p-2 rounded-lg bg-gradient-to-r ${typeInfo.bgColor}`}
          >
            <div
              className={`w-8 h-8 rounded-lg bg-gradient-to-br ${typeInfo.color} flex items-center justify-center`}
            >
              <i className={`fas ${typeInfo.icon} text-white text-sm`}></i>
            </div>
            <span className="text-sm font-medium text-gray-700">
              {typeInfo.title} Account
            </span>
          </div> */}

          {isSignupWithEmail ? (
            /* Email Signup Form */
            <>
              <button
                onClick={() => setIsSignupWithEmail(false)}
                className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
              >
                <i className="fas fa-arrow-left"></i>
                Back
              </button>

              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 text-center">
                Create {typeInfo.title} Account
              </h1>
              <p className="text-gray-500 text-xs sm:text-sm mb-4 text-center">
                {typeInfo.subtitle}
              </p>

              {/* Name Row */}
              <div className="flex gap-2 sm:gap-3 mb-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First name"
                    value={form.firstName}
                    onChange={setInput("firstName")}
                    className={`w-full px-3 py-2 sm:py-2.5 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                      error.firstName ? "border-red-500" : "border-gray-200"
                    }`}
                    autoFocus
                  />
                  {error.firstName && (
                    <span className="text-red-500 text-xs mt-0.5 block">
                      {error.firstName}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last name"
                    value={form.lastName}
                    onChange={setInput("lastName")}
                    className={`w-full px-3 py-2 sm:py-2.5 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                      error.lastName ? "border-red-500" : "border-gray-200"
                    }`}
                  />
                  {error.lastName && (
                    <span className="text-red-500 text-xs mt-0.5 block">
                      {error.lastName}
                    </span>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={form.email}
                  onChange={setInput("email")}
                  className={`w-full px-3 py-2 sm:py-2.5 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error.email ? "border-red-500" : "border-gray-200"
                  }`}
                />
                {error.email && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.email}
                  </span>
                )}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={form.password}
                    onChange={setInput("password")}
                    className={`w-full px-3 py-2 sm:py-2.5 pr-10 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                      error.password ? "border-red-500" : "border-gray-200"
                    }`}
                  />
                  <i
                    className={`fas ${
                      showPassword ? "fa-eye" : "fa-eye-slash"
                    } absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 text-sm`}
                    onClick={() => setShowPassword(!showPassword)}
                  ></i>
                </div>
                {error.password && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.password}
                  </span>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={form.confirmPassword}
                    onChange={setInput("confirmPassword")}
                    className={`w-full px-3 py-2 sm:py-2.5 pr-10 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                      error.confirmPassword
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                  />
                  <i
                    className={`fas ${
                      showConfirmPassword ? "fa-eye" : "fa-eye-slash"
                    } absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 text-sm`}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  ></i>
                </div>
                {error.confirmPassword && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.confirmPassword}
                  </span>
                )}
              </div>

              {/* Captcha */}
              <div className="mb-3 flex justify-center transform scale-90 origin-left">
                <ReCAPTCHA
                  sitekey="6Ledv1skAAAAAF3Zb07lBukAj9N-sfq5wA_C7872"
                  onChange={setCaptcha}
                />
              </div>
              {error.captcha && (
                <span className="text-red-500 text-xs block text-center mb-2">
                  {error.captcha}
                </span>
              )}

              {/* Terms */}
              <div className="mb-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={() => {
                      setIsTermsChecked(!isTermsChecked);
                      if (error.terms) {
                        setError((prev) => ({ ...prev, terms: "" }));
                      }
                    }}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-gray-600">
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setModal("terms")}
                      className="text-indigo-500 hover:underline"
                    >
                      Terms & Conditions
                    </button>
                  </span>
                </label>
                {error.terms && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.terms}
                  </span>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={createValidation}
                disabled={loading}
                className={`w-full py-2.5 sm:py-3 text-sm font-semibold text-white rounded-full transition-all duration-200 mb-4 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-900 hover:bg-gray-800 active:scale-[0.98]"
                }`}
              >
                {loading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
              </button>
            </>
          ) : (
            /* Social Signup Options */
            <>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 text-center">
                Sign up as {typeInfo.title}
              </h1>
              <p className="text-gray-600 mb-2 text-xs sm:text-sm">
                {typeInfo.subtitle}
              </p>

              {/* Toggle Button */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={() => setIsBrandSelected(!isBrandSelected)}
                  className="group relative inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium rounded-full transition-all duration-300 overflow-hidden"
                  style={{
                    background: isBrandSelected
                      ? "linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    boxShadow: isBrandSelected
                      ? "0 4px 12px rgba(236, 72, 153, 0.3)"
                      : "0 4px 12px rgba(16, 185, 129, 0.3)",
                  }}
                >
                  <span className="absolute inset-0 bg-white/20 translate-y-full "></span>
                  <i
                    className={`fas ${isBrandSelected ? "fa-star" : "fa-building"
                      } text-white relative z-10`}
                  ></i>
                  <span className="text-white font-semibold relative z-10">
                    Switch to {isBrandSelected ? "Influencer" : "Brand"}
                  </span>
                </button>
              </div>

              {/* Influencer Note */}
              {!isBrandSelected && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-800">
                    <i className="fas fa-info-circle mr-1"></i>
                    Facebook/Instagram business account required.{" "}
                    <a
                      href="https://tamayou.com/faq"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-500 underline"
                    >
                      Learn how
                    </a>
                  </p>
                </div>
              )}

              {/* Terms Checkbox */}
              <div className="mb-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={() => {
                      setIsTermsChecked(!isTermsChecked);
                      if (error.terms) {
                        setError((prev) => ({ ...prev, terms: "" }));
                      }
                    }}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-gray-600">
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() =>
                        setModal(isBrandSelected ? "terms" : "termsCreate")
                      }
                      className="text-indigo-500 hover:underline"
                    >
                      Terms & Conditions
                    </button>
                  </span>
                </label>
                {error.terms && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.terms}
                  </span>
                )}
              </div>

              {/* Social Buttons */}
              <div className="flex flex-col gap-2 sm:gap-3">
                {socialButtons
                  .filter(
                    ({ provider }) =>
                      !(isBrandSelected && provider === "instagram")
                  )
                  .map(({ provider, label, Icon }) => (
                    <button
                      key={provider}
                      onClick={() => handleSocialLogin(provider)}
                      className="w-full py-2.5 sm:py-3 text-sm font-medium bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 sm:gap-3 transition-all hover:bg-gray-50 hover:border-gray-300"
                    >
                      <Icon />
                      Sign up with {label}
                    </button>
                  ))}

                {/* Email Option - Brand only */}
                {isBrandSelected && (
                  <button
                    onClick={() => setIsSignupWithEmail(true)}
                    className="w-full py-2.5 sm:py-3 text-sm font-medium bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 sm:gap-3 transition-all hover:bg-gray-50 hover:border-gray-300"
                  >
                    <EmailIcon />
                    Sign up with Email
                  </button>
                )}
              </div>
            </>
          )}

          {/* Login Link */}
          <p className="text-center text-xs text-gray-500 mt-1">
            Already have an account?{" "}
            <Link
              to="/account/login"
              className="text-indigo-500 font-medium hover:text-indigo-600"
            >
              Sign In
            </Link>
          </p>

          {/* Footer */}
          <div className="border-t border-gray-100 mt-4 pt-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>© 2026</span>
                <a
                  href="https://tamayou.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-700"
                >
                  Privacy
                </a>
                <a
                  href="https://tamayou.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-700"
                >
                  Terms
                </a>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="https://www.facebook.com/tamayouhub"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fab fa-facebook-f text-sm"></i>
                </a>
                <a
                  href="https://www.instagram.com/tamayouhub/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fab fa-instagram text-sm"></i>
                </a>
                <a
                  href="https://x.com/tamayou"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fab fa-x-twitter text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Toast Messages */}
      {toastList.length > 0 && (
        <Toast list={toastList} setList={setToastList} />
      )}
    </div>
  );
}
